// @flow
import React from 'react';
import classNames from 'classnames';
import glamorous, { Div, Span } from 'glamorous';
import { PlayChip } from 'app/components/styled/currencyIcons';
import DateComponent from '../commons/DateComponent';
import Responsive from '../responsive/Responsive';
import Row from '../commons/Row';
import { APPROVED, COMPLETED, PENDING, REJECTED } from '../../constants/purchaseTypes';
import DetailsList from './DetailsList';

type Props = {
  credentialInfo?: Array<Object>,
  classes: Object,
  createdAt: string,
  expiryDate?: string,
  name: string,
  pin?: string,
  price: number,
  status: {
    id: number,
    name: string,
  },
  voucherCode?: string,
};

const defaultProps = {
  credentialInfo: undefined,
  expiryDate: undefined,
  pin: undefined,
  voucherCode: undefined,
};

const PaddingDiv = glamorous.div(props => ({
  paddingTop: props.theme.spacing.xsmall,
  paddingBottom: props.theme.spacing.xsmall,
  display: 'flex',
  flexDirection: 'column',
}));

const BottomDiv = glamorous(PaddingDiv)(props => ({
  fontSize: props.theme.fontSize.xxsmall,
  padding: props.theme.spacing.xsmall,
}));

const ExpiryDate = glamorous.div(props => ({
  display: 'flex',
  flexWrap: 'wrap',
  paddingTop: 4,
  paddingBottom: 4,
  color: props.theme.color.danger,
  fontSize: props.theme.fontSize.xsmall,
}));

const SmallNotice = glamorous.div(({ theme }) => ({
  fontSize: theme.fontSize.tiny,
  fontStyle: 'italic',
}));

function PurchaseRow({
  price,
  classes,
  createdAt,
  credentialInfo,
  expiryDate,
  pin,
  status,
  name,
  voucherCode,
}: Props) {
  return (
    <Row>
      <PaddingDiv>
        <Div display="flex">
          <div className={classNames(classes.tableCell, classes.store)}>
            <Div display="flex" flexDirection="column">
              <Span paddingBottom={4}>{name}</Span>
              <div
                className={classNames(classes.value, classes.pill, {
                  approved: status.id === APPROVED || status.id === COMPLETED,
                  pending: status.id === PENDING,
                  rejected: status.id === REJECTED,
                })}
              >
                {status.name}
              </div>
              <Responsive desktop tabletLandscape>
                {(status.id === PENDING || status.id === APPROVED) && (
                  <SmallNotice>Delivery of Gift cards can take up to 14 business days</SmallNotice>
                )}
              </Responsive>
            </Div>
          </div>
          <div className={classNames(classes.tableCell, classes.tableCell__small)}>
            <Div display="flex" flexDirection="column">
              <Responsive desktop tabletLandscape>
                {isDesktop => (
                  <DateComponent
                    date={createdAt}
                    format={isDesktop ? 'ddd DD MMM YYYY' : 'DD-MMM-YYYY'}
                  />
                )}
              </Responsive>
              {expiryDate && (
                <ExpiryDate>
                  <Span paddingRight={4}>Expiry:</Span>
                  {expiryDate}
                </ExpiryDate>
              )}
            </Div>
          </div>
          <div className={classNames(classes.tableCell, classes.tableCell__small)}>
            <PlayChip />
            <span>{price}</span>
          </div>
          <Responsive desktop tabletLandscape>
            <div className={classNames(classes.tableCell, classes.tableCell__large)}>
              <DetailsList credentialInfo={credentialInfo} voucherCode={voucherCode} pin={pin} />
            </div>
          </Responsive>
        </Div>
        <Responsive mobile tabletLandscape={false}>
          <BottomDiv>
            {(status.id === PENDING || status.id === APPROVED) && (
              <SmallNotice>Delivery of Gift cards can take up to 14 business days</SmallNotice>
            )}
            <DetailsList credentialInfo={credentialInfo} voucherCode={voucherCode} pin={pin} />
          </BottomDiv>
        </Responsive>
      </PaddingDiv>
    </Row>
  );
}

PurchaseRow.defaultProps = defaultProps;

export default PurchaseRow;
